import { I18N } from "aurelia-i18n";
import { autoinject, bindable, computedFrom } from "aurelia-framework";
import { Reflection } from "../../../../../utilities/reflection";
import { Claims } from '../../../../../constants/authentication/claims';
import { QueryFilter } from '../../../../../models/queries/queries.models';
import { RulesClientService } from '../../../../../services/rules/rules-client.service';
import { DialogServiceConstants } from '../../../../../constants/basics/dialog.constants';
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { RightshipDataGrid } from "../../../../components/rightship-data-grid/rightship-data-grid";
import { RightShipDialogServiceConstants } from '../../../../../constants/basics/rightship-dialog.constants';
import { GenericConfirmationDialogModel } from '../../../../../models/basics/generic-confirmation-dialog-model';
import { NotificationClientService, NotificationType } from '../../../../../services/basics/notification-client.service';

@autoinject
export class RulesHeader {

    @bindable
    dataGrid: RightshipDataGrid;

    canAddRules: boolean = false;
    canRemoveRules: boolean = false;
    canBulkUnassignRuleToACollection: boolean = false;

    constructor(userClaimsService: UserClaimsService,
        private dialogService: DialogClientService,
        private rulesService: RulesClientService,
        private notificationClientService: NotificationClientService,
        private i18N: I18N) {
        this.canAddRules = userClaimsService.hasPermission(Claims.CanAddRules);
        this.canRemoveRules = userClaimsService.hasPermission(Claims.CanRemoveRules);
        this.canBulkUnassignRuleToACollection = userClaimsService.hasPermission(Claims.CanBulkUnassignRuleToACollection);
    }

    activate(model: { datagrid: RightshipDataGrid }) {
        this.dataGrid = model.datagrid;
    }
    
    @computedFrom(Reflection.getPropertyPath<RulesHeader>(z => z.dataGrid),
        Reflection.getPropertyPath<RulesHeader>(z => z.dataGrid.itemsCollection),
        Reflection.getPropertyPath<RulesHeader>(z => z.dataGrid.itemsCollection.length), 
        "dataGrid._specificIds", "dataGrid._specificIds.length")
    get shouldDisableAssign(): boolean {
        return !this.dataGrid || !this.dataGrid.getSelectedRowsCollection() || this.dataGrid.getSelectedRowsCollection().length <= 0;
    }
    
    @computedFrom(Reflection.getPropertyPath<RulesHeader>(z => z.dataGrid),
        Reflection.getPropertyPath<RulesHeader>(z => z.dataGrid.itemsCollection),
        Reflection.getPropertyPath<RulesHeader>(z => z.dataGrid.itemsCollection.length), 
        "dataGrid._specificIds", "dataGrid._specificIds.length")
    get disableEditLinkedCollections(): boolean {
        return !this.dataGrid || !this.dataGrid.getSelectedRowsCollection() || this.dataGrid.getSelectedRowsCollection().length != 1;
    }   

    //Add rule
    addRule() {
        this.dialogService.show(RightShipDialogServiceConstants.URLs.AddRuleDialog, {
            dataGrid: this.dataGrid,
            ruleId: null
        }, true, false, false, 'right');
    }
    
    showAssignCollectionsLinkedToRule() {
        if (this.disableEditLinkedCollections)
            return;

        const rowItem = this.dataGrid.getSelectedRowsCollection()[0];
        
        this.dialogService.show(RightShipDialogServiceConstants.URLs.AssignCollectionsLinkeToRuleDialog, {
            rule: rowItem.item,
            onSave: () => {
                this.dataGrid.unselectAll();
                this.dataGrid.reloadData();
            }
        }, true, false, false, 'right');
    }
    
    public getFilters() {
        let filtersToInclude: QueryFilter[] = [];
        if (this.dataGrid.itemSource.columns) {
            this.dataGrid.itemSource.columns.filter(x => x.isFilterable).forEach(x => {
                if (x.filtersResolver) {
                    const filters = x.filtersResolver(x.filters);
                    if (filters) {
                        filtersToInclude = filtersToInclude.concat(filters);
                    }
                } else {
                    if (x.filters) {
                        filtersToInclude = filtersToInclude.concat(x.filters);
                    }
                }
            });
        }

        this.dataGrid.ensureValues();

        const included: {
            [name: string]: QueryFilter[],
        } = {};

        // Remove filters residing both in initialFilters and filtersToInclude.  Gives precedence to filtersToInclude.
        filtersToInclude.filter(x => x.filterProperty).forEach(x => {
            if (!x.filterProperty.name) {
                return;
            }

            (included[x.filterProperty.name] || (included[x.filterProperty.name] = [])).push(x);
        });

        const initialFilters = this.dataGrid.initialFilters.filter(x => x.filterProperty && x.filterValue != null && x.filterValue != undefined
            && !(Array.isArray(x.filterValue) && x.filterValue.length == 0) // remove filters with empty array values
            && !included[x.filterProperty.name]);
        return initialFilters.concat(filtersToInclude);
    }

    showDeleteDialog() {
        if (this.shouldDisableAssign)
            return;
            
        if ((!this.dataGrid.toggleAll && this.dataGrid.getSpecificIds().length == 0)) {
            this.notificationClientService.show(this.i18N.tr('no-items-selected-text'), "", NotificationType.Warning);
            return;
        }
        const model = new GenericConfirmationDialogModel();
        model.confirm = this.i18N.tr('delete-text');
        model.cancel = this.i18N.tr('cancel-text');
        model.messages.push(this.i18N.tr('are-you-sure-you-want-to-delete'));
        model.title = this.i18N.tr('confirm-delete-text');
        model.onConfirm = () => this.removeRules();
        this.dialogService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, true, false, false, 'right');
    }

    removeRules() {
        return new Promise<void>((resolve, reject) => {
            const selectedRuleConfigIds = this.dataGrid.getSelectedRowsCollection().map(x => x.item.ruleConfigId);
            this.rulesService.deleteRules(selectedRuleConfigIds).then(() => {
                this.dataGrid.reloadData();
                resolve();
            }).catch(() => { reject(); });
        });
    }
}